// The navigation model the integrator hands to AppShell. It filters items by
// RBAC (canAccess list on each resource; the Status group on
// `upgradeprocess/tenants`) and resolves unported areas to portal URLs
// (ADR 0017) before passing them in. The shell only renders.
import type { ReactNode } from 'react'

export interface NavLinkItem {
  id: string
  label: string
  /** Router path (internal) or absolute portal URL (`isExternal`). */
  href: string
  /** Decorative icon (an `MdOutline*` glyph, ADR 0013). */
  icon?: ReactNode
  /** An unported area: a plain same-tab link to the portal, marked "opens portal". */
  isExternal?: boolean
  /** Override the default prefix match on the current pathname. */
  isActive?: (pathname: string) => boolean
  /** Pinned to the bottom of the navigation, above the version (Settings). */
  isPinned?: boolean
}

/** One level of grouping: the portal's "Status" category. */
export interface NavGroupItem {
  id: string
  label: string
  icon?: ReactNode
  /** Heading of the group's panel ("Upgrade process"). Defaults to `label`. */
  title?: string
  children: NavLinkItem[]
}

export type NavItem = NavLinkItem | NavGroupItem

export function isNavGroup(item: NavItem): item is NavGroupItem {
  return 'children' in item
}

/**
 * Prefix match on whole segments: `/tenants` is active on `/tenants`,
 * `/tenants/123/show`, but not on `/tenantsx` or `/upgradeprocess/tenants`.
 * `/` matches only itself.
 */
export function matchesPath(href: string, pathname: string): boolean {
  const base = href.length > 1 ? href.replace(/\/+$/, '') : href
  if (base === '/') return pathname === '/'
  return pathname === base || pathname.startsWith(`${base}/`)
}

export function isLinkActive(item: NavLinkItem, pathname: string): boolean {
  if (item.isExternal) return false
  return item.isActive ? item.isActive(pathname) : matchesPath(item.href, pathname)
}
